/**
 * État partagé du tracker Tealium (rempli par useTealiumContext et lu par
 * buildBaseDataLayer). Permet d'avoir un wrapper synchrone côté tracker sans
 * forcer chaque appelant à passer le membre / la locale.
 */

export type LoginStatus = 'loggedin' | 'loggedout';
export type SiteType = 'web' | 'mobileapp' | 'miniapp';
export type Platform =
  | 'web-desktop'
  | 'web-mobile'
  | 'web-tablet'
  | 'ios'
  | 'android';

export interface TealiumContext {
  visitorId?: string;
  loginStatus: LoginStatus;
  /** Locale courte (ex. fr, en, pt) */
  locale: string;
  /** Pays propagé (ex. south africa) */
  country: string;
  siteType: SiteType;
  /** Plateforme calculée côté client (window matching media) */
  platform: Platform;
  /** Version d'app propagée (site_version) */
  siteVersion: string;
  /** Domaine logique propagé (page_parent_domain) */
  parentDomain: string;
  /** Subscription / loyalty (optionnels, fournis par useTealiumContext) */
  loyaltyPoints?: number;
  loyaltyRedeemed?: number;
}

const defaultContext: TealiumContext = {
  visitorId: undefined,
  loginStatus: 'loggedout',
  locale: 'en',
  country: 'south africa',
  siteType: 'web',
  platform: 'web-desktop',
  siteVersion: '1.0.0',
  parentDomain: 'trace+',
};

let currentContext: TealiumContext = {...defaultContext};

export const setTealiumContext = (patch: Partial<TealiumContext>): void => {
  currentContext = {...currentContext, ...patch};
};

export const getTealiumContext = (): TealiumContext => currentContext;

export const resetTealiumContext = (): void => {
  currentContext = {...defaultContext};
};

/** Détection plateforme côté client. Fallback desktop côté SSR. */
export const detectPlatform = (): Platform => {
  if (typeof window === 'undefined') return 'web-desktop';
  const ua = (navigator.userAgent || '').toLowerCase();
  const width = window.innerWidth || 0;
  const isTabletUa = /ipad|tablet|playbook|silk/.test(ua);
  const isMobileUa = /mobi|iphone|ipod|android.*mobile/.test(ua);
  if (isTabletUa || (width >= 600 && width < 1024 && /android/.test(ua))) {
    return 'web-tablet';
  }
  if (isMobileUa || width < 600) {
    return 'web-mobile';
  }
  return 'web-desktop';
};
